import {useQuery} from "@tanstack/react-query";
import {MenuOrdersApi, MenuOrderStatus} from "@devour/client";
/*
 * Same list the backend's guard uses (`@devour/models`), but web has its own
 * copy here — MenuOrderSuccess imports it from the same place. Not duplicated a
 * third time locally, so the two stay in step.
 */
import {menuOrderFinalizedStatuses} from "@/pages/menu-orders/MenuOrderListPage";
import getConfig from "@/utils/getConfig";
import { useEffect, useRef } from "react";
import {removeMenuOrderByOrderId} from "@/redux/meta/metaActions";
import {useDispatch} from "react-redux";

export const useMenuOrder = (menuOrderId: string, _disableValidationWhenFetched = false) => {
    const dispatch = useDispatch();
    const isFetched = useRef(false);
    // Define the query function directly inside useQuery
    const result = useQuery({
        queryKey: [
            "menu-order",
            menuOrderId,
        ],
        queryFn: async () => {
            if (!menuOrderId) {
                return null;
            }
            try {
                const menuOrder = await new MenuOrdersApi(getConfig()).getMenuOrdersId({
                    id: menuOrderId,
                });

                /*
                 * A spent order is not a cart. `metaStore.menuOrders[merchantId]`
                 * is persisted to localStorage (redux/index.ts whitelist) and is
                 * only cleared when the user reaches a success/fail screen. Someone
                 * who pays but never gets there — tab closed, payment redirect never
                 * returned, back button — keeps the pointer forever, and on their
                 * next visit the app loads a DELIVERED order as their live cart.
                 *
                 * Adding to that cart then PUTs to a finalized order, the backend
                 * answers 403 meaning "this is spent, rebuild it", and the user sees
                 * "An unknown error has occurred. Please contact support" on a
                 * perfectly ordinary add-to-cart. One user hit that ~46 times over
                 * six days because nothing ever cleared the pointer.
                 *
                 * The catch below only fires when the GET throws, and a DELIVERED
                 * order fetches fine at 200 — so it never self-healed. Checking the
                 * status is what actually closes the loop, and it does so before the
                 * 403 is ever reached rather than depending on the recovery path.
                 */
                /*
                 * Clear the POINTER only — always return the order.
                 *
                 * This hook is the single source for the order-success page, the
                 * whole checkout tree, the cart and the top nav. Returning null for
                 * a non-cart order would blank all of them the moment an order
                 * moves past CHECKOUT: MenuOrderSuccess polls every 5s while the
                 * order is not finalized, and PAYMENT is not a finalized status, so
                 * a user who had just paid would sit on a spinner forever looking
                 * at their own completed order.
                 *
                 * The stale-pointer problem is about `metaStore.menuOrders[merchantId]`
                 * being reused as a live cart, not about hiding the order — so the
                 * dispatch is the fix and the return value must be untouched.
                 *
                 * Condition mirrors the backend's own rule in
                 * update_menu_order.ts, which 403s on exactly these statuses.
                 */
                const isSpentOrder = Boolean(menuOrder) && (
                    menuOrderFinalizedStatuses.includes(menuOrder.status) ||
                    menuOrder.status === MenuOrderStatus.PAYMENT
                );
                if (isSpentOrder) {
                    dispatch(removeMenuOrderByOrderId(menuOrderId));
                }

                return menuOrder;
            } catch (error: any) {
                dispatch(removeMenuOrderByOrderId(menuOrderId)); // Remove menuOrder from Redux
                throw error; // Re-throw error for React Query to handle
            }
        },
        // Options object
        enabled: Boolean(menuOrderId), // Conditional fetching based on menuOrderId presence
        retry: false,
    });

    useEffect(() => {
        if (result.isFetchedAfterMount) {
            isFetched.current = true;
        }
    }, [result.isFetchedAfterMount]);

    return {
        data: result.data,
        refetch: result.refetch,
        isFetching: result.isFetching,
        isFetchedAfterMount: result.isFetchedAfterMount,
        isLoading: result.isLoading,
    };
};
